import useWorkRecommendationAccess from '@/hooks/useWorkRecommendationAccess';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useAuthStore, useUIStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalHelper } from '@/hooks/useModalHelper';
import { accountApi } from '@/lib/api';
import { trackEvent } from '@/constants/utils/analytics';
import { BORDER_RADIUS, MESSAGE_LAYOUT, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import useResponsiveness from '@/hooks/useResponsiveness';
import { isDesktopEditorSidebarType } from '@/constants/utils/files';

const DESKTOP_MESSAGE_INSET = 20;

const createStyles = (colors, isXS, messageWidth) => StyleSheet.create({
  bubbleContainer: {
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%',
    alignSelf: 'center',
    paddingHorizontal: isXS ? 16 : DESKTOP_MESSAGE_INSET,
  },
  bubbleContainerInner: {
    flex: 1,
    width: '100%',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 20,
    backgroundColor: colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    gap: 10,
  },
  slimContainer: {
    paddingVertical: 8,
    paddingHorizontal: isXS ? 16 : DESKTOP_MESSAGE_INSET,
    alignSelf: 'center',
    width: '100%',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%',
    gap: 10,
  },
  wideContainer: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: isXS ? 16 : DESKTOP_MESSAGE_INSET,
    alignSelf: 'center',
    width:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%',
    maxWidth: '100%',
    gap: 10,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  headerCopy: {
    flex: 1,
    gap: 4,
  },
  dismiss: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: BORDER_RADIUS.round,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    maxWidth: '100%',
    flexShrink: 1,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      },
    }),
  },
  chipHovered: {
    backgroundColor: colors.solid_color_secondary,
    borderColor: colors.border_color_input,
  },
  chipLabel: {
    flexShrink: 1,
  },

});

const WorkModeSuggestions = React.memo(({
  prompt,
  fileIds = [],
  onSendToWork,
  collectionId,
  disabled = false,
}) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const messageLayout = useUIStore(state => state.messageLayout);
  const messageWidth = useUIStore(state => state.messageWidth);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus === true);
  const needsAuth = useAuthStore(state => !state.user || state.user?.flags?.isGhost === true);
  const workModeSuggestionsEnabled = useAuthStore(
    state => state.user?.preferences?.chat?.workModeSuggestionsEnabled !== false
  );
  const updateUserPreferences = useAuthStore(state => state.updateUserPreferences);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const sendInFlight = useRef(false);
  const access = useWorkRecommendationAccess(prompt, collectionId);

  const styles = useMemo(
    () => createStyles(colors, isXS, messageWidth),
    [colors, isXS, messageWidth]
  );

  const handleSend = useCallback(async () => {
    if (disabled || sendInFlight.current || !access.canLaunch || !isPlus || needsAuth) return;
    if (!onSendToWork) return;
    sendInFlight.current = true;
    trackEvent('work', 'response_work_mode_suggestion_clicked');
    setSending(true);
    try {
      await onSendToWork({ prompt, fileIds });
    } finally {
      sendInFlight.current = false;
      setSending(false);
    }
  }, [disabled, access.canLaunch, isPlus, needsAuth, onSendToWork, prompt, fileIds]);

  const handleHideForever = useCallback(async () => {
    trackEvent('work', 'response_work_mode_suggestion_hidden');
    updateUserPreferences({ chat: { workModeSuggestionsEnabled: false } });
    try {
      await accountApi.updateChatPreferences({ workModeSuggestionsEnabled: false });
    } catch (error) {
      console.error('Error updating work mode suggestions preference:', error);
    }
  }, [updateUserPreferences]);

  const handleDismiss = useCallback(() => {
    if (disabled || sending) return;
    trackEvent('work', 'response_work_mode_suggestion_dismissed');
    setDismissed(true);
    showConfirmationModal({
      title: t('messages.workModeSuggestion.hideForeverTitle'),
      message: t('messages.workModeSuggestion.hideForeverMessage'),
      confirmText: t('messages.workModeSuggestion.hideForeverConfirm'),
      cancelText: t('messages.workModeSuggestion.hideForeverCancel'),
      onConfirm: handleHideForever,
    });
  }, [disabled, sending, showConfirmationModal, t, handleHideForever]);

  if (!isPlus || needsAuth || !workModeSuggestionsEnabled) return null;
  if (isContextSidebarVisible && isDesktopEditorSidebarType(contextSidebarType)) return null;
  if (dismissed) return null;
  if (!prompt?.trim() && !(Array.isArray(fileIds) && fileIds.length)) return null;

  const subtitle = t('messages.workModeSuggestion.newChatSubtitle');
  const sendA11y = t('messages.workModeSuggestion.newChatSend');
  const sendLabel = sending ? t('messages.workModeSuggestion.sending') : sendA11y;

  const actions = access.canLaunch ? (
    <Pressable
      style={({ hovered, pressed }) => [
        styles.chip,
        hovered && styles.chipHovered,
        (pressed || sending || disabled) && { opacity: 0.8 },
      ]}
      onPress={handleSend}
      disabled={disabled || sending}
      accessibilityRole="button"
      accessibilityLabel={sendA11y}
    >
      <Icon name="briefcase" size={12} color={colors.text_color_secondary} />
      <Text size="s" color="secondary" weight="semibold" numberOfLines={1} style={styles.chipLabel}>
        {sendLabel}
      </Text>
      <Icon name="chevron-right" size={10} color={colors.text_color_tertiary} />
    </Pressable>
  ) : null;

  const content = (
    <>
      <View style={styles.headerRow}>
        <Icon name="briefcase" size={14} color={colors.text_color_primary} />
        <View style={styles.headerCopy}>
          <Text color="primary" size="m" weight="medium">
            {t('messages.workModeSuggestion.title')}
          </Text>
          <Text color="secondary" size="s">
            {subtitle}
          </Text>
          {access.notice ? <Text color="secondary" size="s">{t(`messages.workModeSuggestion.access.${access.notice}`)}</Text> : null}
        </View>
        <Pressable
          onPress={handleDismiss}
          accessibilityRole="button"
          accessibilityLabel={t('common.dismiss')}
          hitSlop={8}
          style={({ hovered, pressed }) => [
            styles.dismiss,
            (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
          ]}
        >
          <Icon name="xmark" size={12} color={colors.text_color_tertiary} />
        </Pressable>
      </View>
      {actions}
    </>
  );

  if (messageLayout === MESSAGE_LAYOUT.SLIM) {
    return <View style={styles.slimContainer}>{content}</View>;
  }

  if (messageLayout === MESSAGE_LAYOUT.WIDE) {
    return <View style={styles.wideContainer}>{content}</View>;
  }

  return (
    <View style={styles.bubbleContainer}>
      <View style={styles.bubbleContainerInner}>
        {content}
      </View>
    </View>
  );
});

WorkModeSuggestions.propTypes = {
  prompt: PropTypes.string,
  collectionId: PropTypes.string,
  fileIds: PropTypes.arrayOf(PropTypes.string),
  onSendToWork: PropTypes.func,
  disabled: PropTypes.bool,
};

WorkModeSuggestions.displayName = 'WorkModeSuggestions';

export default WorkModeSuggestions;
